import React from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { CustomAppConfig, CustomAppLevel } from '../../../../../types/customApps';
import { ReplitIntegration } from '../../../../reducers/tools/ReplitReducer';
import DrawerHookForm from '../../../global/drawer/DrawerHookForm';
import FormSelect from '../../../global/Forms/FormSelect';
import FormInput from '../../../global/Forms/FormInput';
import FormSwitch from '../../../global/Forms/FormSwitch';
import { ApplicationState } from '../../../../reducers';
import { isReceiver } from '../../../../utils/UserUtils';

interface CustomAppsDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: any) => Promise<void>;
  availableApps: ReplitIntegration[];
  editingConfig: CustomAppConfig | null;
  loading: boolean;
  existingConfigs: CustomAppConfig[];
}

type FormData = {
  app_id: number;
  level: CustomAppLevel;
  display_name?: string;
  display_order: number;
  is_enabled: boolean;
};

const CustomAppsDrawer: React.FC<CustomAppsDrawerProps> = ({
  visible,
  onClose,
  onSubmit,
  availableApps,
  editingConfig,
  loading,
  existingConfigs,
}) => {
  const { t } = useTranslation();
  const [formResetTrigger, setFormResetTrigger] = React.useState(0);

  const user = useSelector((state: ApplicationState) => state.user.user);
  const isReceiverUser = React.useMemo(() => user && isReceiver(user), [user]);

  // For receiver users, only show ACCOUNT and PRODUCT levels
  const levels: CustomAppLevel[] = isReceiverUser
    ? ['ACCOUNT', 'PRODUCT']
    : ['ACCOUNT', 'BRAND', 'RECEIVER', 'PRODUCT'];

  const levelOptions = levels.map(level => ({
    id: level,
    name: t(`customApps:levels.${level}`),
  }));

  const appOptions = availableApps.map(app => ({
    id: app.id,
    name: app.app_id,
  }));

  const getValidationSchema = () => {
    return Yup.object().shape({
      app_id: Yup.number().required(t('customApps:messages.selectAppFirst')),
      level: Yup.string().required(t('customApps:messages.selectLevelFirst')),
      display_order: Yup.number()
        .nullable()
        .when('level', ([level], schema) => {
          return schema.test(
            'check-duplicate-display-order',
            t('customApps:messages.duplicateDisplayOrder'),
            (value, context) => {
              if (!value || !level) return true;

              const duplicate = existingConfigs.find(
                config =>
                  config.level === level &&
                  config.display_order === value &&
                  (!editingConfig || config.id !== editingConfig.id)
              );

              if (duplicate) {
                return context.createError({
                  message: t('customApps:messages.duplicateDisplayOrderDetail', {
                    order: value,
                    level,
                  }),
                });
              }

              return true;
            }
          );
        }),
      is_enabled: Yup.boolean(),
    });
  };

  const getDefaultValues = (): FormData => ({
    app_id: editingConfig?.app_id || (undefined as any),
    level: editingConfig?.level || (undefined as any),
    display_name: editingConfig?.display_name || '',
    display_order: editingConfig?.display_order || 1,
    is_enabled: editingConfig?.is_enabled ?? true,
  });

  const handleClose = () => {
    setFormResetTrigger(prev => prev + 1);
    onClose();
  };

  const handleSubmit = async (values: FormData) => {
    await onSubmit(values);
    handleClose();
  };

  return (
    <DrawerHookForm
      key={`${editingConfig?.id || 'new-custom-app-config'}-${formResetTrigger}`}
      title={editingConfig ? t('customApps:editConfiguration') : t('customApps:addConfiguration')}
      visible={visible}
      onClose={handleClose}
      width={500}
      validationSchema={getValidationSchema()}
      defaultValues={getDefaultValues()}
      onSubmit={handleSubmit}
      isLoading={loading}
      destroyOnClose
    >
      {() => {
        return (
          <Form layout="vertical">
            <FormSelect
              name="app_id"
              label={t('customApps:app')}
              values={appOptions}
              placeholder={t('customApps:selectApp')}
              disabled={!!editingConfig}
              required
              useHookForm
            />

            <FormSelect
              name="level"
              label={t('customApps:level')}
              values={levelOptions}
              placeholder={t('customApps:selectLevel')}
              required
              useHookForm
            />

            <FormInput
              name="display_name"
              label={t('customApps:displayName')}
              placeholder={t('customApps:displayNamePlaceholder')}
              useHookForm
            />

            <FormInput
              name="display_order"
              label={t('customApps:displayOrder')}
              type="number"
              min={1}
              placeholder="1"
              useHookForm
            />

            <div className="flex">
              <FormSwitch
                name="is_enabled"
                checkedChildren={t('common:yes')}
                unCheckedChildren={t('common:no')}
                useHookForm
              />
              <span className="h-8 ml-3 leading-8">{t('customApps:enabled')}</span>
            </div>
          </Form>
        );
      }}
    </DrawerHookForm>
  );
};

export default CustomAppsDrawer;
